import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { fmtDate, timeRange, fmtPrice } from "@/lib/events";

function isMinor(dob) {
  if (!dob) return false;
  const age = (Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000);
  return age < 18;
}

export default function Enroll() {
  const { lookupKey, eventId } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [event, setEvent] = useState(null);
  const [pname, setPname] = useState("");
  const [dob, setDob] = useState("");
  const [guardianConsent, setGuardianConsent] = useState(false);
  const [photoRelease, setPhotoRelease] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (eventId) {
      api.get("/events").then(({ data }) => setEvent(data.find((e) => e.id === eventId) || null)).catch(() => {});
      return;
    }
    api.get("/products").then(({ data }) => setProduct(data.find((p) => p.lookup_key === lookupKey))).catch(() => {});
  }, [lookupKey, eventId]);
  const priceLabel = event?.price_cents ? fmtPrice(event.price_cents) : product?.price_display;
  const heading = event ? event.title : product ? product.name : "Enroll";

  const minor = isMinor(dob);

  const startCheckout = async () => {
    if (!user) { toast.error("Please log in first to enroll."); return; }
    if (!pname || !dob) { toast.error("Add the participant's name and date of birth."); return; }
    if (minor && !guardianConsent) { toast.error("Guardian consent is required for a minor."); return; }
    setBusy(true);
    try {
      const { data: participant } = await api.post("/participants", { name: pname, date_of_birth: dob, notes: "" });
      await api.post("/consent", { participant_id: participant.id, type: "photo_video_release", granted: photoRelease, version: "1.0" });
      if (minor) {
        await api.post("/consent", { participant_id: participant.id, type: "guardian_consent", granted: true, version: "1.0" });
      }
      const { data } = await api.post("/payments/checkout", {
        ...(eventId ? { event_id: eventId } : { lookup_key: lookupKey }),
        quantity: 1, origin_url: window.location.origin, participant_id: participant.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div data-testid="enroll-page">
      <PageHeader eyebrow={event ? "Event sign-up" : "Enrollment"} title={heading}
        subtitle={event ? `${fmtDate(event.date)}${timeRange(event) ? ` · ${timeRange(event)} ET` : ""}${event.location_name ? ` · ${event.location_name}` : ""}`
          : "Set up the participant and capture consent before checkout. The paying account is separate from the participant profile."} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {!user && (
          <div className="mb-6 bg-[#ffdedf] border border-[#bf6163] rounded-xl p-4 text-[#550001] font-semibold" data-testid="enroll-login-notice">
            You need to be logged in to enroll. <a href={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="underline">Log in or create an account.</a>
          </div>
        )}
        {product?.register_url && (
          <div className="mb-6 bg-[#f9f6f6] border border-[#dfd8da] rounded-xl p-4 text-sm text-[#1a1617]" data-testid="enroll-register-form-notice">
            <strong>Registration and payment happen separately.</strong> After paying here, please also complete the{" "}
            <a href={product.register_url} target="_blank" rel="noreferrer" className="font-semibold text-[#920e12] underline" data-testid="enroll-register-form-link">registration form</a>.
          </div>
        )}
        <div className="bg-white rounded-2xl border border-[#dfd8da] p-8 space-y-5">
          <div>
            <Label htmlFor="p-name">Participant name</Label>
            <input id="p-name" value={pname} onChange={(e) => setPname(e.target.value)} data-testid="enroll-participant-name"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]" />
          </div>
          <div>
            <Label htmlFor="p-dob">Date of birth</Label>
            <input id="p-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="enroll-dob"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]" />
            {dob && (
              <p className="mt-1 text-sm font-semibold text-[#920e12]" data-testid="enroll-minor-flag">
                {minor ? "Minor — guardian consent required" : "Adult participant"}
              </p>
            )}
          </div>

          {minor && (
            <label className="flex items-start gap-3 bg-[#f9f6f6] rounded-lg p-4 border border-[#dfd8da] cursor-pointer">
              <Checkbox checked={guardianConsent} onCheckedChange={(v) => setGuardianConsent(!!v)} data-testid="enroll-guardian-consent" className="mt-0.5" />
              <span className="text-sm text-[#1a1617]">
                <strong>Guardian consent.</strong> I am the parent or legal guardian and I consent to this participant's enrollment.
              </span>
            </label>
          )}

          <label className="flex items-start gap-3 bg-[#f9f6f6] rounded-lg p-4 border border-[#dfd8da] cursor-pointer">
            <Checkbox checked={photoRelease} onCheckedChange={(v) => setPhotoRelease(!!v)} data-testid="enroll-photo-release" className="mt-0.5" />
            <span className="text-sm text-[#1a1617]">
              <strong>Photo / video release.</strong> I allow photos and video of this participant during class. This is
              recorded with a timestamp and version, shown on the director's roster, and can be revoked anytime.
            </span>
          </label>

          <div className="flex items-center gap-2 text-xs text-[#545454]">
            <ShieldCheck size={16} className="text-[#15803d]" /> Consent is stored with timestamp + version and is fully revocable.
          </div>

          <Button onClick={startCheckout} disabled={busy || !user} className="w-full h-12 bg-[#920e12] hover:bg-[#550001] text-white font-bold text-base" data-testid="enroll-checkout-btn">
            {busy ? "Redirecting to secure checkout…" : `Continue to payment${priceLabel ? ` · ${priceLabel}` : ""}`}
          </Button>
          <p className="text-center text-xs text-[#545454]">Secure checkout via Stripe · promo codes supported · test card 4242 4242 4242 4242</p>
        </div>
      </div>
    </div>
  );
}
